'use client'

type Props = {
  className?: string
  label?: string | null
  tone?: 'dark' | 'light'
}

// Inline SVG card brand badges — keeps bundle small, no external deps, and
// won't 404 if a CDN goes down. Each badge is a flat tile with the brand
// wordmark / glyph approximation. Underwriting just needs to see clearly
// which brands the merchant accepts.

function VisaBadge() {
  return (
    <svg viewBox="0 0 48 30" className="h-7 w-auto" aria-label="Visa" role="img">
      <rect width="48" height="30" rx="4" fill="#1A1F71" />
      <text
        x="24"
        y="20"
        textAnchor="middle"
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="900"
        fontStyle="italic"
        fontSize="12"
        fill="#FFFFFF"
        letterSpacing="1"
      >
        VISA
      </text>
    </svg>
  )
}

function MastercardBadge() {
  return (
    <svg viewBox="0 0 48 30" className="h-7 w-auto" aria-label="Mastercard" role="img">
      <rect width="48" height="30" rx="4" fill="#FFFFFF" stroke="#E5E7EB" />
      <circle cx="20" cy="15" r="7" fill="#EB001B" />
      <circle cx="28" cy="15" r="7" fill="#F79E1B" />
      <path d="M24 9.5a7 7 0 0 0 0 11 7 7 0 0 0 0-11Z" fill="#FF5F00" />
    </svg>
  )
}

function AmexBadge() {
  return (
    <svg viewBox="0 0 48 30" className="h-7 w-auto" aria-label="American Express" role="img">
      <rect width="48" height="30" rx="4" fill="#2E77BC" />
      <text
        x="24"
        y="14"
        textAnchor="middle"
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="900"
        fontSize="6"
        fill="#FFFFFF"
        letterSpacing="0.5"
      >
        AMERICAN
      </text>
      <text
        x="24"
        y="22"
        textAnchor="middle"
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="900"
        fontSize="6"
        fill="#FFFFFF"
        letterSpacing="0.5"
      >
        EXPRESS
      </text>
    </svg>
  )
}

function DiscoverBadge() {
  return (
    <svg viewBox="0 0 48 30" className="h-7 w-auto" aria-label="Discover" role="img">
      <rect width="48" height="30" rx="4" fill="#FFFFFF" stroke="#E5E7EB" />
      <text
        x="22"
        y="19"
        textAnchor="middle"
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="900"
        fontSize="7"
        fill="#0F1B2D"
      >
        DISCOVER
      </text>
      <circle cx="40" cy="15" r="4" fill="#F58220" />
    </svg>
  )
}

export function AcceptedCards({ className = '', label, tone = 'dark' }: Props) {
  const labelText = label === null ? null : label ?? 'Pagos aceptados'
  const labelColor = tone === 'dark' ? 'text-gray-400' : 'text-gray-500'

  return (
    <div className={`inline-flex flex-col items-center gap-2 ${className}`}>
      {labelText && (
        <span className={`text-[11px] uppercase tracking-wider font-semibold ${labelColor}`}>
          {labelText}
        </span>
      )}
      <div className="flex items-center gap-2">
        <VisaBadge />
        <MastercardBadge />
        <AmexBadge />
        <DiscoverBadge />
      </div>
    </div>
  )
}
